import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { MediaQuery } from '@grandvisionhq/common';
import { useCartDiscounts, sortByMinimumQuantityAscending } from '@grandvisionhq/fun-products';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { getAttrValue } from '@grandvisionhq/utils';
import { pushPdpEventToDataLayer, pushTabClickEvent } from '../../utils/DataLayer/data-layer.js';
import { viewProductSdEvent } from '../../utils/structured-data.js';
import { useProductViewHistory } from '../../utils/use-product-view-history.js';
import { useLenses } from './hooks.js';
import Lenses$1 from './Lenses.js';
import { useLensBundle } from './LensBundles/context.js';
import { CTAButton } from './components/CTAButton/CTAButton.js';

const LensesContainer = ({ addToCartRef , allowBackOrder , deliveryResolverFactory , maxOrderableQuantity , onCTAClick , onPriceChange , opticianForm , product , showStickyButton , stickyTop , variant , renderAddToCartButton , handleConversionEvent ,  })=>{
    const { excludedFromCheckout , promotionalText  } = variant;
    const { masterVariant  } = product;
    const { currency , getLabel , locale  } = useIntl();
    const { isContactLensEnabled: isPrescriptionEnabled , isDeliveryFullDateEnabled , isCartDiscountsEnabled , isProductLabelEnabled , isPrescriptionInfoEnabled ,  } = useFeatureToggles();
    const brandKey = String(getAttrValue(masterVariant, 'brandKey')) // aka "variantGroup"
    ;
    const { cartDiscounts  } = useCartDiscounts(brandKey, isCartDiscountsEnabled);
    const sortedCartDiscounts = cartDiscounts.sort(sortByMinimumQuantityAscending);
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const productViewHistory = useProductViewHistory();
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    /**
   * CLBB way of handling subscriptions, currently in use by the BeNe opco's.
   */ const isCLBBTabSelected = activeTabIndex === 1;
    const [lensState, lensActions] = useLenses({
        product,
        cartDiscounts,
        isPrescriptionEnabled,
        isPrescriptionInfoEnabled,
        variant
    });
    const { lineItems: bundleLineItems , pricePerMonth  } = useLensBundle();
    const lineItemsForCart = isCLBBTabSelected ? bundleLineItems : lensState.priceTotals.selectedProducts;
    /**
   * Add SKU to view history
   * Only adds the masterProduct so we don't get duplicates
   */ React.useEffect(()=>{
        if (Boolean(getAttrValue(masterVariant, 'masterProduct')) && masterVariant.sku) {
            productViewHistory.add(masterVariant.sku);
        }
    }, []);
    /**
   * Callback to update the AddToCartNotification in ProductDetail
   * should execute when either the solutions or the lenses update
   */ React.useEffect(()=>{
        onPriceChange(lensState.priceTotals);
    }, [
        onPriceChange,
        lensState.priceTotals
    ]);
    /**
   * Triggers the product_detail SD action when the user clicks on the CLBB subscription tab
   */ React.useEffect(()=>{
        viewProductSdEvent({
            product,
            activeSKU: variant.sku,
            url: window.location.href,
            locale,
            isCLBBTabSelected,
            pricePerMonth
        });
        const purchaseType = isCLBBTabSelected ? DataLayer.DataLayerTypes.PurchaseType.CLBBSubscription : DataLayer.DataLayerTypes.PurchaseType.OneTime;
        const productType = isCLBBTabSelected ? DataLayer.DataLayerTypes.LineItemType.Bundle : DataLayer.DataLayerTypes.LineItemType.Single;
        pushPdpEventToDataLayer(purchaseType, product, variant.sku, currency, productType, lensState.clbbPrice);
    }, [
        activeTabIndex
    ]) // eslint-disable-line react-hooks/exhaustive-deps
    ;
    const setActiveTab = (index)=>{
        pushTabClickEvent(!isCLBBTabSelected);
        setActiveTabIndex(index);
    };
    const ctaButton = /*#__PURE__*/ React.createElement(CTAButton, {
        calculatedDiscountPrice: lensState.priceTotals.calculatedDiscountPrice,
        calculatedPrice: lensState.priceTotals.calculatedPrice,
        lineItemsForCart: lineItemsForCart,
        excludedFromCheckout: excludedFromCheckout,
        isPrescriptionEnabled: isPrescriptionEnabled,
        isCLBBSubscription: isCLBBTabSelected,
        hasLenses: lensState.hasLenses,
        onPrescriptionClick: lensActions.handlePrescriptionClick,
        handleMiniCartChange: lensActions.handleMiniCartChange,
        onClick: onCTAClick,
        pricePerMonth: pricePerMonth,
        renderAddToCartButton: renderAddToCartButton,
        handleConversionEvent: handleConversionEvent
    });
    const contactLensesConfiguratorConfig = {
        allowBackOrder,
        formValidations: lensState.formValidations,
        handleFormChange: lensActions.handleFormChange,
        maxOrderableQuantity,
        opticianForm,
        promotionalText,
        quantityDiscountItems: sortedCartDiscounts
    };
    let deliveryIndicatorConfig = {
        fallbackLabel: getLabel('products.deliveryIndicator.selectPrescription'),
        className: 'product-detail__delivery-indicator'
    };
    if (isDeliveryFullDateEnabled) {
        deliveryIndicatorConfig = swcHelpers.objectSpread({}, deliveryIndicatorConfig, {
            items: lineItemsForCart.map((item)=>({
                    sku: item.sku,
                    quantity: item.quantity || 1
                })
            )
        });
    } else {
        deliveryIndicatorConfig = swcHelpers.objectSpread({}, deliveryIndicatorConfig, {
            // TODO: Add availability info when bundles have that
            availability: !isCLBBTabSelected ? lensState.availability : [
                undefined
            ],
            deliveryResolver: deliveryResolverFactory && deliveryResolverFactory(lensState.selectedLensVariants)
        });
    }
    const labels = {
        perBox: getLabel('products.perBox'),
        perMonth: getLabel('common.frequency.perMonth'),
        descriptionTitle: getLabel('products.contactLenses.header.details'),
        clbbSubscriptionTitle: getLabel('products.contactLenses.subscription.title'),
        oneTimeTitle: getLabel('products.contactLenses.oneTime.title'),
        lens: getLabel('products.unit.contactLens'),
        lenses: getLabel('products.unit.contactLenses')
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Lenses$1, {
        addToCartRef: addToCartRef,
        contactLensesConfiguratorConfig: contactLensesConfiguratorConfig,
        ctaButton: ctaButton,
        currency: currency,
        deliveryIndicatorConfig: deliveryIndicatorConfig,
        isSmallScreen: isSmallScreen,
        isProductLabelEnabled: isProductLabelEnabled,
        labels: labels,
        packPrice: lensState.packPrice,
        clbbPrice: lensState.clbbPrice,
        showStickyButton: showStickyButton,
        stickyTop: stickyTop,
        variant: variant,
        activeTabIndex: activeTabIndex,
        setActiveTabIndex: setActiveTab,
        isCLBBTabSelected: isCLBBTabSelected
    }));
};
var Lenses = LensesContainer;

export { LensesContainer, Lenses as default };
//# sourceMappingURL=LensesContainer.js.map
